// Visual call-flow designer — drag-and-drop nodes that compile to Lua source.
// Uses a minimal home-grown flow renderer to avoid an extra react-flow dependency
// in this skeleton. Graduate to react-flow / xyflow when polishing.
//
// Node types:
//   answer, play, gather (DTMF), transfer, hangup, branch (eval condition),
//   queue, voicemail, set_var, lua_raw

import { useState, useMemo } from 'react';
import { Save, Trash2, Plus, Code } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type NodeType = 'answer'|'play'|'gather'|'transfer'|'hangup'|'branch'|'queue'|'voicemail'|'set_var'|'lua_raw';
type Node = {
  id: string;
  type: NodeType;
  x: number; y: number;
  data: Record<string, any>;
  next?: string;            // default next-node id
  branches?: { match: string; next: string }[]; // for branch / gather
};

const NODE_DEFAULTS: Record<NodeType, Partial<Node>> = {
  answer:    { data: {} },
  play:      { data: { file: 'ivr/ivr-welcome.wav' } },
  gather:    { data: { prompt: 'ivr/ivr-please_enter_extension.wav', timeout: 5000, max_digits: 4 }, branches: [] },
  transfer:  { data: { dest: '100', context: 'XML' } },
  hangup:    { data: {} },
  branch:    { data: { condition: 'true' }, branches: [] },
  queue:     { data: { queue: 'support' } },
  voicemail: { data: { ext: '${destination_number}' } },
  set_var:   { data: { name: 'foo', value: 'bar' } },
  lua_raw:   { data: { code: '-- raw lua here' } },
};

function compile(nodes: Node[], startId: string | null): string {
  const lines: string[] = [
    '-- Generated by Stratus CallFlow Designer',
    'session:answer()',
    '',
  ];
  function emit(id: string | undefined, depth = 0): void {
    if (!id) return;
    const n = nodes.find(x => x.id === id);
    if (!n) return;
    const ind = '  '.repeat(depth);
    switch (n.type) {
      case 'answer':    lines.push(`${ind}session:answer()`); break;
      case 'play':      lines.push(`${ind}session:streamFile("${n.data.file}")`); break;
      case 'gather':
        lines.push(`${ind}local d = session:playAndGetDigits(1, ${n.data.max_digits || 4}, 3, ${n.data.timeout || 5000}, "#", "${n.data.prompt}", "ivr/ivr-that_was_an_invalid_entry.wav", "[0-9*#]")`);
        for (const b of (n.branches || [])) {
          lines.push(`${ind}if d == "${b.match}" then`);
          emit(b.next, depth + 1);
          lines.push(`${ind}end`);
        }
        break;
      case 'transfer':  lines.push(`${ind}session:transfer("${n.data.dest}", "${n.data.context || 'XML'}", session:getVariable("domain_name"))`); break;
      case 'hangup':    lines.push(`${ind}session:hangup()`); break;
      case 'branch':
        lines.push(`${ind}if ${n.data.condition} then`);
        for (const b of (n.branches || [])) {
          emit(b.next, depth + 1);
        }
        lines.push(`${ind}end`);
        break;
      case 'queue':     lines.push(`${ind}session:execute("callcenter", "${n.data.queue}@" .. session:getVariable("domain_name"))`); break;
      case 'voicemail': lines.push(`${ind}session:execute("voicemail", "default " .. session:getVariable("domain_name") .. " ${n.data.ext}")`); break;
      case 'set_var':   lines.push(`${ind}session:setVariable("${n.data.name}", "${n.data.value}")`); break;
      case 'lua_raw':   lines.push(...String(n.data.code || '').split('\n').map(l => ind + l)); break;
    }
    if (n.next) emit(n.next, depth);
  }
  emit(startId || nodes[0]?.id);
  lines.push('', 'session:hangup()');
  return lines.join('\n');
}

export default function CallFlowDesigner() {
  const qc = useQueryClient();
  const [name, setName] = useState('new_flow');
  const [description, setDescription] = useState('');
  const [nodes, setNodes] = useState<Node[]>([
    { id: 'n1', type: 'answer', x: 60, y: 40, data: {}, next: 'n2' },
    { id: 'n2', type: 'play',   x: 60, y: 140, data: { file: 'ivr/ivr-welcome.wav' }, next: 'n3' },
    { id: 'n3', type: 'gather', x: 60, y: 240, data: { prompt: 'ivr/ivr-please_enter_extension.wav' }, branches: [] },
  ]);
  const [selectedId, setSelectedId] = useState<string | null>('n3');
  const lua = useMemo(() => compile(nodes, nodes[0]?.id || null), [nodes]);
  const selected = nodes.find(n => n.id === selectedId);

  const save = useMutation({
    mutationFn: () => api.callflow.create({ name, description, lua_source: lua, enabled: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['callflow'] }),
  });

  function addNode(type: NodeType) {
    const id = `n${Date.now()}`;
    setNodes(p => [...p, { id, type, x: 200, y: 60 + p.length * 70, ...(NODE_DEFAULTS[type] as any), data: { ...(NODE_DEFAULTS[type].data || {}) } }]);
    setSelectedId(id);
  }
  function updateSelected(patch: Partial<Node>) {
    if (!selected) return;
    setNodes(p => p.map(n => n.id === selectedId ? { ...n, ...patch, data: { ...n.data, ...(patch.data || {}) } } : n));
  }
  function deleteSelected() {
    if (!selected) return;
    setNodes(p => p.filter(n => n.id !== selectedId).map(n => ({
      ...n,
      next: n.next === selectedId ? undefined : n.next,
      branches: (n.branches || []).filter(b => b.next !== selectedId),
    })));
    setSelectedId(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <h1 className="text-2xl font-semibold">Call Flow Designer</h1>
        <input className="input max-w-xs" placeholder="flow_name" value={name} onChange={e => setName(e.target.value)} />
        <input className="input flex-1 min-w-48" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save flow</button>
      </div>
      <div className="grid grid-cols-12 gap-4 h-[80vh]">
        <aside className="col-span-2 card overflow-auto">
          <h3 className="font-semibold mb-2 text-sm">Add node</h3>
          <div className="space-y-1">
            {(['answer','play','gather','transfer','hangup','branch','queue','voicemail','set_var','lua_raw'] as NodeType[]).map(t => (
              <button key={t} onClick={() => addNode(t)} className="btn-ghost w-full text-left text-sm flex items-center gap-2">
                <Plus size={12} /> {t}
              </button>
            ))}
          </div>
        </aside>
        <div className="col-span-6 card relative overflow-auto" onClick={() => setSelectedId(null)}>
          <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%">
            {nodes.map(n => n.next ? <Edge key={`${n.id}->${n.next}`} from={n} to={nodes.find(x => x.id === n.next)} /> : null)}
            {nodes.flatMap(n => (n.branches || []).map(b => <Edge key={`${n.id}->${b.next}-${b.match}`} from={n} to={nodes.find(x => x.id === b.next)} label={b.match} />))}
          </svg>
          {nodes.map(n => (
            <div key={n.id} onClick={(e) => { e.stopPropagation(); setSelectedId(n.id); }}
              style={{ left: n.x, top: n.y }}
              className={`absolute w-32 p-2 rounded-md border cursor-pointer text-xs ${selectedId === n.id ? 'bg-primary text-white border-primary' : 'bg-elevated border-border'}`}>
              <div className="font-semibold uppercase text-[10px] opacity-70">{n.type}</div>
              <div className="truncate">{Object.values(n.data || {})[0] as any || ''}</div>
            </div>
          ))}
        </div>
        <aside className="col-span-4 card overflow-auto">
          {selected ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm uppercase">{selected.type}</h3>
                <button onClick={deleteSelected} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
              </div>
              {Object.entries(selected.data).map(([k, v]) => (
                <div key={k}>
                  <label className="block text-xs text-textSecondary mb-1">{k}</label>
                  {k === 'code' ? (
                    <textarea className="input font-mono text-xs h-32" value={String(v ?? '')}
                      onChange={e => updateSelected({ data: { [k]: e.target.value } })} />
                  ) : (
                    <input className="input" value={String(v ?? '')}
                      onChange={e => updateSelected({ data: { [k]: e.target.value } })} />
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs text-textSecondary mb-1">Next node</label>
                <select className="input" value={selected.next || ''} onChange={e => updateSelected({ next: e.target.value || undefined })}>
                  <option value="">(end)</option>
                  {nodes.filter(n => n.id !== selected.id).map(n => <option key={n.id} value={n.id}>{n.type} ({n.id})</option>)}
                </select>
              </div>
              {(selected.type === 'gather' || selected.type === 'branch') && (
                <BranchesEditor selected={selected} nodes={nodes}
                  onChange={(branches) => updateSelected({ branches })} />
              )}
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-textSecondary flex items-center gap-2">
                  <Code size={12} /> Compiled Lua
                </summary>
                <pre className="text-[10px] font-mono bg-bg p-2 mt-2 overflow-x-auto">{lua}</pre>
              </details>
            </div>
          ) : (
            <div className="text-textSecondary text-sm">Click a node to edit, or pick a type from the left to add.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Edge({ from, to, label }: any) {
  if (!from || !to) return null;
  const x1 = from.x + 64, y1 = from.y + 30;
  const x2 = to.x + 64,   y2 = to.y + 30;
  const midY = (y1 + y2) / 2;
  return (
    <g>
      <path d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
        stroke="#3a3d47" strokeWidth="2" fill="none" />
      {label && <text x={(x1 + x2) / 2} y={midY - 4} fill="#7a7f8a" fontSize="11" textAnchor="middle">{label}</text>}
    </g>
  );
}

function BranchesEditor({ selected, nodes, onChange }: any) {
  const branches = selected.branches || [];
  return (
    <div>
      <label className="block text-xs text-textSecondary mb-1">Branches</label>
      {branches.map((b: any, i: number) => (
        <div key={i} className="flex gap-1 mb-1">
          <input className="input flex-1" value={b.match} placeholder="DTMF / condition"
            onChange={e => onChange(branches.map((x: any, j: number) => j === i ? { ...x, match: e.target.value } : x))} />
          <select className="input flex-1" value={b.next || ''}
            onChange={e => onChange(branches.map((x: any, j: number) => j === i ? { ...x, next: e.target.value } : x))}>
            <option value="">(end)</option>
            {nodes.filter((n: any) => n.id !== selected.id).map((n: any) => <option key={n.id} value={n.id}>{n.type} ({n.id})</option>)}
          </select>
          <button className="btn-ghost p-1" onClick={() => onChange(branches.filter((_: any, j: number) => j !== i))}><Trash2 size={12} /></button>
        </div>
      ))}
      <button className="btn-ghost text-xs" onClick={() => onChange([...branches, { match: '', next: '' }])}>+ Add branch</button>
    </div>
  );
}
